import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  getApiV3CustomerFavoritesCookbook,
} from '@hungryroot/rest';

export function getFavoritesCookbookQueryKey() {
  return ['Customers', 'Favorites Cookbook'] as const;
}

/**
 * Filter-scoped child key. Each occasion bucket caches independently so tab
 * switches are instant on return. Invalidating the prefix above invalidates
 * every variant at once.
 */
export function getFavoritesCookbookFilterQueryKey(filter: string | null) {
  return [...getFavoritesCookbookQueryKey(), filter ?? 'all'] as const;
}

type OptionType = { enabled?: boolean; filter?: string | null };

export function useFavoritesCookbook(options?: OptionType) {
  const { filter = null, ...queryOptions } = options ?? {};

  return useQuery({
    queryKey: getFavoritesCookbookFilterQueryKey(filter),
    queryFn: async () => {
      const resp = await getApiV3CustomerFavoritesCookbook(
        filter != null ? { filter } : {}
      );

      if (resp.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse('Get Favorites Cookbook', {
          status: resp.status,
          error: JSON.stringify(resp.data),
          key: 'useFavoritesCookbook',
        });

        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...queryOptions,
    placeholderData: keepPreviousData,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}
